import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import { Form, Formik } from "formik";
import { merge } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import ChildStep2Schema from "./ChildStep2Schema";
import * as actions from "../../../store/modules/register/registerActions";
import { RootState } from "../../../store/store";
import { DocumentTypes, IChild } from "../../../types/types";
import { getStyleColor } from "../../../utils/helpers";
import Immunisation from "../../Children/Immunisation";
import { ButtonPrimary, ButtonSecondary } from "../../Common/Buttons/Buttons";
import Pager from "../../Common/Pager/Pager";

interface Props {
  child: IChild;
  id: string;
}

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "white",
  },
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  formLegend: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: theme.spacing(4),
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    marginBottom: theme.spacing(2),
  },
  formLabel: {
    marginBottom: theme.spacing(2),
  },
  heading: {
    marginTop: 0,
    marginBottom: 0,
  },
  subHeading: {
    fontSize: "16px",
    color: getStyleColor("--custom-color-dark-grey"),
  },
  formControl: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  selectEmpty: {
    marginBottom: theme.spacing(2),
  },
  textField: { marginBottom: theme.spacing(2) },
  container: { paddingLeft: 0 },
}));

function FormChildStep2({ child, id }: Props) {
  const immunisationDocuments = useSelector((state: RootState) =>
    state.register?.documents.filter(
      (document) =>
        document.registrationChildUniqueId === child.uniqueId &&
        document.documentTypeId === DocumentTypes.ImmunizationRecords
    )
  );
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const navigate = useNavigate();

  function updateChild(values: IChild) {
    actions.saveChild(dispatch, values);
    navigate(`/registration-child-step/3/${id}`);
  }

  const formikValues = {
    hasBeenImmunised: undefined,
  };
  const initialValues = merge(formikValues, child);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={ChildStep2Schema}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(() => {
          setSubmitting(false);
          updateChild(values);
        }, 500);
      }}
    >
      {({ setFieldValue, submitForm, isSubmitting, values, touched, errors, handleChange, handleBlur }) => (
        <Form>
          <Grid container className={classes.root} spacing={6}>
            <Grid item xs={12} md={12}>
              <Immunisation
                classes={classes}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                touched={touched}
                values={values}
                errors={errors}
                immunisationDocuments={immunisationDocuments}
              />
            </Grid>
          </Grid>
          <Pager>
            <ButtonSecondary href={`/registration-child-step/1/${id}`}>
              <strong>Back</strong>
            </ButtonSecondary>
            <ButtonPrimary disabled={isSubmitting} onClick={submitForm}>
              <strong>Next</strong>
            </ButtonPrimary>
          </Pager>
          <Backdrop className={classes.backdrop} open={isSubmitting}>
            <CircularProgress color="primary" />
          </Backdrop>
        </Form>
      )}
    </Formik>
  );
}

export default FormChildStep2;
